import { Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { of, Observable } from 'rxjs';
import { LanguageConstants } from './utils/constants/language.constants';
import { getTranslation as getTranslationFromStore } from './translation-store.interface';
import { LanguageFallbackStrategyService } from './services/i18n/language-fallback-strategy.service';

@Injectable({ providedIn: 'root' })
export class TranslocoBundledLoader implements TranslocoLoader {
  constructor(private langFallBack: LanguageFallbackStrategyService) {}

  getTranslation(lang: string): Observable<Translation> {
    if (!lang || typeof lang !== 'string') {
      return of({});
    }

    const scopeMatch = lang.match(/^(.+)\/(.+)$/);
    if (scopeMatch) {
      const [, scope, language] = scopeMatch;

      if (!LanguageConstants.SUPPORTED_LANGUAGE_CODES.includes(language)) {
        return of({});
      }

      const translations = this.mergeScopeWithFallbacks(language, scope);

      return of(translations);
    }

    if (!LanguageConstants.SUPPORTED_LANGUAGE_CODES.includes(lang)) {
      return of({});
    }

    const translations = this.mergeScopeWithFallbacks(lang, 'root');
    return of(translations);
  }

  getScopedTranslation(scope: string, lang: string): Observable<Translation> {
    if (!lang || typeof lang !== 'string') {
      return of({});
    }

    if (!LanguageConstants.SUPPORTED_LANGUAGE_CODES.includes(lang)) {
      return of({});
    }

    if (!scope || typeof scope !== 'string') {
      return of({});
    }

    const translations = getTranslationFromStore(lang, scope);
    return of(translations);
  }

  private mergeIfMissing(target: any, source: any): any {
    if (!source || typeof source !== 'object') return target;
    if (!target || typeof target !== 'object') return { ...source };
    const result: any = { ...target };
    for (const key of Object.keys(source)) {
      if (result[key] === undefined) {
        result[key] = source[key];
      }
    }
    return result;
  }

  private mergeScopeWithFallbacks(
    language: string,
    scope: string
  ): Record<string, any> {
    let translations = getTranslationFromStore(language, scope);
    const fallbacks = this.langFallBack.getFallbacks(language) || [];
    for (const fb of fallbacks) {
      if (!LanguageConstants.SUPPORTED_LANGUAGE_CODES.includes(fb)) continue;
      const fbScope = getTranslationFromStore(fb, scope);
      translations = this.mergeIfMissing(translations, fbScope);
    }
    return translations;
  }
}
